import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiSettings, FiX, FiEye, FiMouse, FiCheck, FiMonitor, FiRefreshCw, 
  FiShield, FiBarChart3, FiHeart, FiAlertTriangle, FiCheckCircle 
} = FiIcons;

// Accessibility Checker Utility
class AccessibilityChecker {
  constructor() {
    this.issues = [];
    this.passed = [];
  }

  // Check color contrast ratios
  checkColorContrast() {
    const contrastIssues = [];
    
    // Get all text elements
    const textElements = document.querySelectorAll('p,h1,h2,h3,h4,h5,h6,span,a,button,label,li');
    
    textElements.forEach(element => {
      const styles = window.getComputedStyle(element);
      const backgroundColor = styles.backgroundColor;
      const color = styles.color;
      
      // Skip transparent backgrounds
      if (backgroundColor === 'rgba(0,0,0,0)' || backgroundColor === 'transparent') {
        return;
      }
      
      const contrast = this.calculateContrastRatio(color, backgroundColor);
      const fontSize = parseFloat(styles.fontSize);
      const fontWeight = styles.fontWeight;
      
      // WCAG AA requirements: 4.5:1 for normal text, 3:1 for large text
      const isLargeText = fontSize >= 18 || (fontSize >= 14 && (fontWeight === 'bold' || fontWeight >= 700));
      const requiredRatio = isLargeText ? 3 : 4.5;
      
      if (contrast < requiredRatio) {
        contrastIssues.push({
          element: element.tagName,
          text: element.textContent?.substring(0, 50) + '...',
          contrast: contrast.toFixed(2),
          required: requiredRatio,
          foreground: color,
          background: backgroundColor,
          selector: this.getSelector(element)
        });
      }
    });
    
    return contrastIssues;
  }

  // Calculate contrast ratio between two colors
  calculateContrastRatio(color1, color2) {
    const getLuminance = (color) => {
      const rgb = this.parseColor(color);
      const [r, g, b] = rgb.map(c => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    };

    const lum1 = getLuminance(color1);
    const lum2 = getLuminance(color2);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    
    return (brightest + 0.05) / (darkest + 0.05);
  }

  // Parse color string to RGB values
  parseColor(color) {
    const div = document.createElement('div');
    div.style.color = color;
    document.body.appendChild(div);
    const computedColor = window.getComputedStyle(div).color;
    document.body.removeChild(div);
    
    const match = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    return match ? [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])] : [0, 0, 0];
  }

  // Check keyboard navigation
  checkKeyboardNavigation() {
    const issues = [];
    
    const interactiveElements = document.querySelectorAll(
      'a,button,input,textarea,select,[tabindex],[role="button"],[role="link"],[role="tab"]'
    );
    
    interactiveElements.forEach((element, index) => {
      // Check if element is focusable
      const tabIndex = element.getAttribute('tabindex');
      const isNativelyFocusable = ['A', 'BUTTON', 'INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName);
      
      if (!isNativelyFocusable && (tabIndex === null || parseInt(tabIndex) < 0)) {
        issues.push({
          element: element.tagName,
          issue: 'Interactive element not focusable',
          selector: this.getSelector(element)
        });
      }
      
      // Check for focus indicators
      const hasCustomFocus = element.classList.contains('focus-ring') || 
                            element.style.outline || 
                            element.style.boxShadow;
      
      if (!hasCustomFocus) {
        issues.push({
          element: element.tagName,
          issue: 'Missing focus indicator',
          selector: this.getSelector(element)
        });
      }
    });
    
    return issues;
  }

  // Check ARIA attributes
  checkARIAAttributes() {
    const issues = [];
    
    // Check for missing ARIA labels on interactive elements without text
    const interactiveElements = document.querySelectorAll(
      'button,a,input[type="submit"],input[type="button"]'
    );
    
    interactiveElements.forEach(element => {
      const hasText = element.textContent?.trim();
      const hasAriaLabel = element.getAttribute('aria-label');
      const hasAriaLabelledby = element.getAttribute('aria-labelledby');
      const hasTitle = element.getAttribute('title');
      
      if (!hasText && !hasAriaLabel && !hasAriaLabelledby && !hasTitle) {
        issues.push({
          element: element.tagName,
          issue: 'Interactive element missing accessible name',
          selector: this.getSelector(element)
        });
      }
    });
    
    // Check for proper heading hierarchy
    const headings = document.querySelectorAll('h1,h2,h3,h4,h5,h6');
    let previousLevel = 0;
    
    headings.forEach(heading => {
      const currentLevel = parseInt(heading.tagName.charAt(1));
      if (currentLevel > previousLevel + 1) {
        issues.push({
          element: heading.tagName,
          issue: `Heading level skips from h${previousLevel} to h${currentLevel}`,
          selector: this.getSelector(heading)
        });
      }
      previousLevel = currentLevel;
    });
    
    // Check for images without alt text
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      if (!img.hasAttribute('alt')) {
        issues.push({
          element: 'IMG',
          issue: 'Image missing alt attribute',
          selector: this.getSelector(img)
        });
      }
    });
    
    return issues;
  }

  // Check form accessibility
  checkFormAccessibility() {
    const issues = [];
    
    const formControls = document.querySelectorAll('input,textarea,select');
    formControls.forEach(control => {
      const id = control.getAttribute('id');
      const ariaLabel = control.getAttribute('aria-label');
      const ariaLabelledby = control.getAttribute('aria-labelledby');
      
      // Check for associated label
      let hasLabel = false;
      if (id) {
        const label = document.querySelector(`label[for="${id}"]`);
        hasLabel = !!label;
      }
      
      if (!hasLabel && !ariaLabel && !ariaLabelledby) {
        issues.push({
          element: control.tagName,
          issue: 'Form control missing label',
          selector: this.getSelector(control)
        });
      }
      
      // Check for required field indicators
      if (control.hasAttribute('required')) {
        const label = document.querySelector(`label[for="${id}"]`);
        if (label && !label.textContent.includes('*') && !label.querySelector('.required')) {
          issues.push({
            element: control.tagName,
            issue: 'Required field not clearly indicated',
            selector: this.getSelector(control)
          });
        }
      }
    });
    
    return issues;
  }

  // Generate CSS selector for element
  getSelector(element) {
    if (element.id) return `#${element.id}`;
    if (element.className) return `.${element.className.split(' ')[0]}`;
    return element.tagName.toLowerCase();
  }

  // Run complete audit
  async runAudit() {
    const results = {
      colorContrast: this.checkColorContrast(),
      keyboardNavigation: this.checkKeyboardNavigation(),
      ariaAttributes: this.checkARIAAttributes(),
      formAccessibility: this.checkFormAccessibility(),
      timestamp: new Date().toISOString()
    };
    
    // Calculate overall score
    const totalIssues = Object.values(results).reduce((sum, issues) => {
      return sum + (Array.isArray(issues) ? issues.length : 0);
    }, 0);
    
    results.score = Math.max(0, 100 - (totalIssues * 5));
    results.level = this.getComplianceLevel(results.score);
    
    return results;
  }

  getComplianceLevel(score) {
    if (score >= 95) return 'AAA';
    if (score >= 85) return 'AA';
    if (score >= 70) return 'A';
    return 'Non-compliant';
  }
}

// Accessibility Manager Component
const AccessibilityManager = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [auditResults, setAuditResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [showDetails, setShowDetails] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  
  const [settings, setSettings] = useState({
    customCursor: true,
    reducedMotion: false,
    highContrast: false,
    largeFocus: false,
    textSize: 'normal',
    keyboardNavigation: true,
    screenReaderOptimized: false
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    if (isOpen) {
      // Load current preferences
      const savedSettings = localStorage.getItem('accessibility-settings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        setSettings(parsed);
      }
      
      // Run audit when settings panel opens
      runAudit();
      
      setHasChanges(false);
    }
  }, [isOpen]);

  // Detect system preferences
  useEffect(() => {
    detectSystemPreferences();
  }, []);

  const detectSystemPreferences = () => {
    const mediaQueries = {
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)'),
      highContrast: window.matchMedia('(prefers-contrast: high)'),
      colorScheme: window.matchMedia('(prefers-color-scheme: dark)')
    };

    // Apply system preferences if no user settings exist
    const savedSettings = localStorage.getItem('accessibility-settings');
    if (!savedSettings) {
      const systemSettings = {
        ...settings,
        reducedMotion: mediaQueries.reducedMotion.matches,
        highContrast: mediaQueries.highContrast.matches
      };
      setSettings(systemSettings);
      applySettings(systemSettings);
    }
  };

  // Apply settings to document
  const applySettings = (newSettings) => {
    const root = document.documentElement;

    // Custom cursor setting
    if (newSettings.customCursor) {
      root.classList.remove('disable-custom-cursor');
    } else {
      root.classList.add('disable-custom-cursor');
    }

    // High contrast setting
    if (newSettings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    // Large focus indicators
    if (newSettings.largeFocus) {
      root.classList.add('large-focus');
    } else {
      root.classList.remove('large-focus');
    }

    // Reduced motion
    if (newSettings.reducedMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }

    // Text size
    root.classList.remove('text-size-small', 'text-size-normal', 'text-size-large', 'text-size-xl');
    root.classList.add(`text-size-${newSettings.textSize}`);

    // Keyboard navigation enhancement
    if (newSettings.keyboardNavigation) {
      root.classList.add('enhanced-keyboard');
    } else {
      root.classList.remove('enhanced-keyboard');
    }

    // Screen reader optimization
    if (newSettings.screenReaderOptimized) {
      root.classList.add('screen-reader-optimized');
    } else {
      root.classList.remove('screen-reader-optimized');
    }

    // Announce changes to screen readers
    announceSettingChange(newSettings);
  };

  // Announce setting changes to screen readers
  const announceSettingChange = (newSettings) => {
    const announcer = document.getElementById('accessibility-announcer') || document.createElement('div');
    if (!announcer.id) {
      announcer.id = 'accessibility-announcer';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'sr-only';
      document.body.appendChild(announcer);
    }
    announcer.textContent = 'Accessibility settings updated';
  };

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    setHasChanges(true);
    applySettings(newSettings);
    localStorage.setItem('accessibility-settings', JSON.stringify(newSettings));
  };

  const saveSettings = () => {
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
    applySettings(settings);
    setHasChanges(false);
    
    // Show success message
    const event = new CustomEvent('toast', {
      detail: { message: 'Accessibility preferences updated successfully', type: 'success' }
    });
    window.dispatchEvent(event);
  };

  const resetToDefaults = () => {
    const defaults = {
      customCursor: true,
      reducedMotion: false,
      highContrast: false,
      largeFocus: false,
      textSize: 'normal',
      keyboardNavigation: true,
      screenReaderOptimized: false
    };
    setSettings(defaults);
    setHasChanges(true);
    applySettings(defaults);
    localStorage.setItem('accessibility-settings', JSON.stringify(defaults));
  };

  // Run accessibility audit
  const runAudit = async () => {
    setIsRunning(true);
    const checker = new AccessibilityChecker();
    
    // Wait a moment for DOM to be ready
    setTimeout(async () => {
      const results = await checker.runAudit();
      setAuditResults(results);
      setIsRunning(false);
    }, 500);
  };

  const getScoreColor = (score) => {
    if (score >= 95) return 'text-green-600';
    if (score >= 85) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreIcon = (score) => {
    if (score >= 85) return FiCheckCircle;
    if (score >= 70) return FiAlertTriangle;
    return FiX;
  };

  const toggleDetails = (section) => {
    setShowDetails(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const accessibilityOptions = [
    {
      key: 'customCursor',
      title: 'Custom Cursor',
      description: 'Disable custom cursor for standard system cursor',
      icon: FiMouse,
      inverted: true,
      category: 'interaction'
    },
    {
      key: 'reducedMotion',
      title: 'Reduce Motion',
      description: 'Minimize animations and transitions for vestibular sensitivity',
      icon: FiEye,
      category: 'motion'
    },
    {
      key: 'highContrast',
      title: 'High Contrast',
      description: 'Increase contrast for better visibility',
      icon: FiEye,
      category: 'visual'
    },
    {
      key: 'largeFocus',
      title: 'Enhanced Focus',
      description: 'Larger, more visible focus indicators for keyboard navigation',
      icon: FiEye,
      category: 'navigation'
    },
    {
      key: 'keyboardNavigation',
      title: 'Enhanced Keyboard Navigation',
      description: 'Improved keyboard shortcuts and navigation aids',
      icon: FiEye,
      category: 'navigation'
    },
    {
      key: 'screenReaderOptimized',
      title: 'Screen Reader Optimization',
      description: 'Optimize interface for screen reader users',
      icon: FiEye,
      category: 'assistive'
    }
  ];

  const textSizeOptions = [
    { value: 'small', label: 'Small (90%)', description: 'Smaller text size' },
    { value: 'normal', label: 'Normal (100%)', description: 'Default text size' },
    { value: 'large', label: 'Large (125%)', description: 'Larger text for better readability' },
    { value: 'xl', label: 'Extra Large (150%)', description: 'Extra large text for visual impairments' }
  ];

  const auditSections = [
    {
      key: 'colorContrast',
      title: 'Color Contrast',
      description: 'WCAG 2.2 contrast ratio requirements'
    },
    {
      key: 'keyboardNavigation',
      title: 'Keyboard Navigation',
      description: 'Focus management and keyboard accessibility'
    },
    {
      key: 'ariaAttributes',
      title: 'ARIA Attributes',
      description: 'Semantic markup and screen reader support'
    },
    {
      key: 'formAccessibility',
      title: 'Form Accessibility',
      description: 'Form labels, validation, and user guidance'
    }
  ];

  return (
    <>
      {/* Settings Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-primary text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 focus-ring z-40 flex items-center justify-center"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Open accessibility settings"
      >
        <SafeIcon icon={FiSettings} className="w-6 h-6" />
      </motion.button>

      {/* Settings Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, x: 400 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 400 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 overflow-y-auto"
              role="dialog"
              aria-labelledby="accessibility-settings-title"
              aria-describedby="accessibility-settings-description"
            >
              {/* Header */}
              <div className="bg-primary text-white p-6 flex items-center justify-between">
                <div>
                  <h2 id="accessibility-settings-title" className="text-xl font-semibold">
                    Accessibility Settings
                  </h2>
                  <p id="accessibility-settings-description" className="text-purple-200 text-sm mt-1">
                    Customize your experience for better accessibility
                  </p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors focus-ring"
                  aria-label="Close accessibility settings"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 px-6">
                <nav className="flex space-x-8" aria-label="Accessibility settings tabs">
                  <button
                    onClick={() => setActiveTab('settings')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors focus-ring ${
                      activeTab === 'settings' 
                        ? 'border-primary text-primary' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    aria-current={activeTab === 'settings' ? 'page' : undefined}
                  >
                    Settings
                  </button>
                  <button
                    onClick={() => setActiveTab('audit')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors focus-ring ${
                      activeTab === 'audit' 
                        ? 'border-primary text-primary' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    aria-current={activeTab === 'audit' ? 'page' : undefined}
                  >
                    Audit
                  </button>
                </nav>
              </div>

              {/* Content */}
              <div className="p-6">
                {activeTab === 'settings' ? (
                  <div className="space-y-8">
                    {/* Text Size */}
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg mb-4">Text Size</h3>
                      <div className="grid grid-cols-1 gap-3">
                        {textSizeOptions.map((option) => (
                          <label
                            key={option.value}
                            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-primary ${
                              settings.textSize === option.value
                                ? 'border-primary bg-primary/5'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="textSize"
                              value={option.value}
                              checked={settings.textSize === option.value}
                              onChange={(e) => updateSetting('textSize', e.target.value)}
                              className="sr-only"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{option.label}</div>
                              <div className="text-sm text-gray-600">{option.description}</div>
                            </div>
                            {settings.textSize === option.value && (
                              <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary" />
                            )}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Toggle Options */}
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg mb-4">
                        Display & Interaction
                      </h3>
                      <div className="space-y-4">
                        {accessibilityOptions.map((option) => {
                          const isEnabled = option.inverted 
                            ? !settings[option.key] 
                            : settings[option.key];
                          
                          return (
                            <div
                              key={option.key}
                              className="flex items-start justify-between p-4 bg-gray-50 rounded-lg"
                            >
                              <div className="flex items-start space-x-3 flex-1">
                                <div className="p-2 bg-white rounded-lg shadow-sm">
                                  <SafeIcon icon={option.icon} className="w-5 h-5 text-gray-600" />
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900">{option.title}</h4>
                                  <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => updateSetting(
                                  option.key, 
                                  option.inverted ? !settings[option.key] : !settings[option.key]
                                )}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-ring ${
                                  isEnabled ? 'bg-primary' : 'bg-gray-200'
                                }`}
                                role="switch"
                                aria-checked={isEnabled}
                                aria-labelledby={`${option.key}-label`}
                              >
                                <span className="sr-only">{option.title}</span>
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    isEnabled ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* System Integration */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <SafeIcon icon={FiMonitor} className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-blue-800">System Integration</h4>
                          <p className="text-blue-700 text-sm mt-1">
                            These settings work alongside your operating system's accessibility features. 
                            We automatically detect and respect your system preferences when available.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-3">
                      <button
                        onClick={resetToDefaults}
                        className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors focus-ring font-medium"
                      >
                        Reset to Defaults
                      </button>
                      <button
                        onClick={saveSettings}
                        disabled={!hasChanges}
                        className="flex-1 py-3 px-4 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors focus-ring font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Save Settings
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Accessibility Audit Results
                      </h3>
                      <button
                        onClick={runAudit}
                        disabled={isRunning}
                        className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors focus-ring disabled:opacity-50"
                      >
                        <SafeIcon
                          icon={FiRefreshCw}
                          className={`w-4 h-4 ${isRunning ? 'animate-spin' : ''}`}
                        />
                        <span>{isRunning ? 'Running...' : 'Re-run Audit'}</span>
                      </button>
                    </div>

                    {isRunning ? (
                      <div className="text-center py-8">
                        <SafeIcon
                          icon={FiRefreshCw}
                          className="w-8 h-8 text-primary animate-spin mx-auto mb-4"
                        />
                        <p className="text-gray-600">Running accessibility audit...</p>
                      </div>
                    ) : auditResults ? (
                      <div className="space-y-6">
                        {/* Overall Score */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-900">Overall Score</h4>
                              <p className="text-sm text-gray-600">
                                Compliance Level: {auditResults.level}
                              </p>
                            </div>
                            <div className="text-right">
                              <div className={`text-2xl font-bold ${getScoreColor(auditResults.score)}`}>
                                {auditResults.score}/100
                              </div>
                              <SafeIcon
                                icon={getScoreIcon(auditResults.score)}
                                className={`w-6 h-6 ${getScoreColor(auditResults.score)} mx-auto mt-1`}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Audit Sections */}
                        <div className="space-y-4">
                          {auditSections.map((section) => {
                            const issues = auditResults[section.key] || [];
                            const hasIssues = issues.length > 0;
                            
                            return (
                              <div key={section.key} className="border border-gray-200 rounded-lg">
                                <button
                                  onClick={() => toggleDetails(section.key)}
                                  className="w-full p-4 text-left hover:bg-gray-50 transition-colors focus-ring"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <SafeIcon
                                        icon={hasIssues ? FiAlertTriangle : FiCheckCircle}
                                        className={`w-5 h-5 ${hasIssues ? 'text-yellow-500' : 'text-green-500'}`}
                                      />
                                      <div>
                                        <h5 className="font-medium text-gray-900">
                                          {section.title}
                                        </h5>
                                        <p className="text-sm text-gray-600">
                                          {section.description}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className={`text-sm font-medium ${hasIssues ? 'text-yellow-600' : 'text-green-600'}`}>
                                        {hasIssues ? `${issues.length} issues` : 'Passed'}
                                      </span>
                                      <SafeIcon
                                        icon={FiEye}
                                        className={`w-4 h-4 text-gray-400 transform transition-transform ${
                                          showDetails[section.key] ? 'rotate-180' : ''
                                        }`}
                                      />
                                    </div>
                                  </div>
                                </button>
                                
                                {showDetails[section.key] && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="border-t border-gray-200 p-4 bg-gray-50"
                                  >
                                    {hasIssues ? (
                                      <div className="space-y-3">
                                        {issues.map((issue, index) => (
                                          <div
                                            key={index}
                                            className="bg-white p-3 rounded border-l-4 border-yellow-400"
                                          >
                                            <div className="flex items-start justify-between">
                                              <div>
                                                <h6 className="font-medium text-gray-900 text-sm">
                                                  {issue.element || 'Element'}
                                                </h6>
                                                <p className="text-sm text-gray-600 mt-1">
                                                  {issue.issue || issue.text}
                                                </p>
                                                {issue.selector && (
                                                  <p className="text-xs text-gray-500 mt-1 font-mono">
                                                    {issue.selector}
                                                  </p>
                                                )}
                                              </div>
                                              {issue.contrast && (
                                                <div className="text-right text-xs text-gray-500">
                                                  <div>Contrast: {issue.contrast}:1</div>
                                                  <div>Required: {issue.required}:1</div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-center py-4">
                                        <SafeIcon
                                          icon={FiCheckCircle}
                                          className="w-8 h-8 text-green-500 mx-auto mb-2"
                                        />
                                        <p className="text-sm text-green-600 font-medium">
                                          All checks passed for {section.title}
                                        </p>
                                      </div>
                                    )}
                                  </motion.div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Recommendations */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-800 mb-2">
                            Accessibility Recommendations
                          </h4>
                          <ul className="text-sm text-blue-700 space-y-1">
                            <li>• Ensure all interactive elements have visible focus indicators</li>
                            <li>• Maintain color contrast ratios of at least 4.5:1 for normal text</li>
                            <li>• Provide alternative text for all meaningful images</li>
                            <li>• Use semantic HTML and proper heading hierarchy</li>
                            <li>• Test with keyboard navigation and screen readers</li>
                            <li>• Consider users with cognitive disabilities and motor impairments</li>
                          </ul>
                        </div>
                        
                        <div className="text-xs text-gray-500 text-center">
                          Last audit: {new Date(auditResults.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Screen reader announcements */}
      <div id="accessibility-announcer" className="sr-only" aria-live="polite" aria-atomic="true" />
    </>
  );
};

export default AccessibilityManager;